import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { bbox } from "@turf/turf";

export default function Map({ routes, eventRoutes, revealCount, activitiesRevealCount, selectedAthlete, showEvent, showTraining }) {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const mapLoaded = useRef(false);
    const eventRoutesRef = useRef([]);
    const routesRef = useRef([]);
    const fitTimeout = useRef(null);
    const tooltipRef = useRef(null);
    const activitiesRevealCountRef = useRef(null);
    const [hovered, setHovered] = useState(null);

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    };

    const getSpeed = (distanceMeters, movingTimeSeconds) => {
        const hours = movingTimeSeconds / 3600;
        if (hours === 0) return "0.0";
        return ((distanceMeters / 1000) / hours).toFixed(1);
    };

    // ── Filters ──────────────────────────────────────────────
    useEffect(() => {
        if (!mapInstance.current || !mapLoaded.current) return;
        setFilter(selectedAthlete, showTraining, showEvent, mapInstance);
    }, [selectedAthlete, showTraining, showEvent]);

    // ── Activiteiten laden ────────────────────────────────────
    useEffect(() => {
        routesRef.current = routes;
        if (!mapInstance.current) {
            mapInstance.current = new maplibregl.Map({
                container: mapRef.current,
                style: "https://tiles.openfreemap.org/styles/liberty"
            });

            mapInstance.current.on("load", () => {
                mapLoaded.current = true;
                // Vaste laagvolgorde: event-routes (onder) → routes (boven)
                // Events altijd leeg starten — reveal-effect vult aan.
                // Routes: gebruik ref zodat ook activiteiten die vóór map-load aankwamen de count respecteren.
                addEventRoutes(mapInstance.current, []);
                const initCount = activitiesRevealCountRef.current;
                addRoutes(mapInstance.current, initCount != null
                    ? routesRef.current.slice(0, initCount)
                    : routesRef.current);
                scheduleFitAll(mapInstance.current);

                let hoveredId = null;

                mapInstance.current.on("mouseenter", "routes", () => {
                    mapInstance.current.getCanvas().style.cursor = "pointer";
                });

                mapInstance.current.on("mousemove", "routes", (e) => {
                    if (e.features.length === 0) {
                        if (hoveredId !== null) {
                            mapInstance.current.setFeatureState({ source: "routes", id: hoveredId }, { hover: false });
                        }
                        hoveredId = null;
                        setHovered(null);
                        return;
                    }
                    const newId = e.features[0].id;
                    if (hoveredId !== null && hoveredId !== newId) {
                        mapInstance.current.setFeatureState({ source: "routes", id: hoveredId }, { hover: false });
                    }
                    hoveredId = newId;
                    mapInstance.current.setFeatureState({ source: "routes", id: hoveredId }, { hover: true });
                    setHovered(e.features[0].properties);

                    // Tooltip positie direct via DOM — geen React re-render
                    if (tooltipRef.current) {
                        tooltipRef.current.style.left = `${e.originalEvent.clientX + 16}px`;
                        tooltipRef.current.style.top = `${e.originalEvent.clientY + 16}px`;
                    }
                });

                mapInstance.current.on("mouseleave", "routes", () => {
                    setHovered(null);
                    mapInstance.current.getCanvas().style.cursor = "";
                    if (hoveredId !== null) {
                        mapInstance.current.setFeatureState({ source: "routes", id: hoveredId }, { hover: false });
                    }
                    hoveredId = null;
                });

                setFilter(selectedAthlete, showTraining, showEvent, mapInstance);
            });
        } else if (mapLoaded.current) {
            // Toon slice als reveal actief is; volledige list als reveal niet gebruikt wordt
            const toShow = activitiesRevealCount != null
                ? routes.slice(0, activitiesRevealCount)
                : routes;
            addRoutes(mapInstance.current, toShow);
            scheduleFitAll(mapInstance.current);
        }
    }, [routes]);

    // Reveal-effect activities: ref bijwerken + display, geen zoom
    useEffect(() => {
        activitiesRevealCountRef.current = activitiesRevealCount;
        if (!mapInstance.current || !mapLoaded.current || activitiesRevealCount == null) return;
        addRoutes(mapInstance.current, (routesRef.current || []).slice(0, activitiesRevealCount));
    }, [activitiesRevealCount]);

    // ── Event routes laden ────────────────────────────────────
    // Effect 1: volledige lijst — alleen voor zoom. Toon niets totdat revealCount start.
    useEffect(() => {
        eventRoutesRef.current = eventRoutes;
        if (!mapInstance.current || !mapLoaded.current) return;
        scheduleFitAll(mapInstance.current);                  // zoom naar alles meteen
        addEventRoutes(mapInstance.current, []);              // verberg alles; reveal doet de rest
    }, [eventRoutes]);

    // Effect 2: reveal — toon routes 1 voor 1, geen zoom.
    useEffect(() => {
        if (!mapInstance.current || !mapLoaded.current) return;
        const slice = revealCount != null
            ? (eventRoutesRef.current || []).slice(0, revealCount)
            : (eventRoutesRef.current || []);
        addEventRoutes(mapInstance.current, slice);
    }, [revealCount]);

    // ── Zoom ──────────────────────────────────────────────────
    function scheduleFitAll(map) {
        if (fitTimeout.current) clearTimeout(fitTimeout.current);
        // 250 ms debounce: als activiteiten en event routes dicht op elkaar binnenkomen,
        // wordt er maar één keer gefitBounds aangeroepen — met beide datasets.
        fitTimeout.current = setTimeout(() => fitAll(map), 250);
    }

    function fitAll(map) {
        const activityFeatures = (routesRef.current || [])
            .filter(r => r.points?.length > 1)
            .map(r => ({
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: r.points.map(p => {
                        let lon = p[1];
                        if (lon > 180) lon -= 360;
                        if (lon < -180) lon += 360;
                        return [lon, p[0]];
                    })
                }
            }));

        const eventFeatures = (eventRoutesRef.current || [])
            .filter(r => r.coordinates?.length > 1)
            .map(r => ({ type: "Feature", geometry: { type: "LineString", coordinates: r.coordinates } }));

        const all = [...activityFeatures, ...eventFeatures];
        if (all.length === 0) return;

        const bounds = bbox({ type: "FeatureCollection", features: all });
        map.fitBounds([[bounds[0], bounds[1]], [bounds[2], bounds[3]]], { padding: 50, maxZoom: 10 });
    }

    // ── Helpers ───────────────────────────────────────────────
    function setFilter(selectedAthlete, showTraining, showEvent, mapInstance) {
        const filters = ["all"];
        if (selectedAthlete !== "all") filters.push(["==", ["get", "athlete"], selectedAthlete]);
        if (!showTraining) filters.push(["!=", ["get", "isTraining"], true]);
        if (!showEvent) filters.push(["!=", ["get", "isTraining"], false]);
        mapInstance.current.setFilter("routes", filters);
    }

    function addEventRoutes(map, eventRoutes) {
        const features = (eventRoutes || [])
            .filter(r => r.coordinates?.length > 1)
            .map(r => ({
                type: "Feature",
                properties: { name: r.name, dayNumber: r.dayNumber, color: r.color },
                geometry: { type: "LineString", coordinates: r.coordinates }
            }));
        const geojson = { type: "FeatureCollection", features };

        if (map.getSource("event-routes")) {
            map.getSource("event-routes").setData(geojson);
        } else {
            map.addSource("event-routes", { type: "geojson", data: geojson });
            map.addLayer({ id: "event-routes", type: "line", source: "event-routes",
                paint: { "line-color": ["get", "color"], "line-width": 5, "line-opacity": 0.7 }
            });
        }
    }

    function addRoutes(map, routes) {
        const features = (routes || []).map(r => ({
            type: "Feature",
            id: r.id,
            properties: {
                name: r.name, displayName: r.displayName,
                date: r.date, description: r.description,
                duration: r.duration, athlete: r.athlete,
                profilePhoto: r.profilePhoto, isTraining: r.isTraining,
                distance: (Number(r.distance) / 1000).toFixed(2),
            },
            geometry: {
                type: "LineString",
                coordinates: r.points.map(p => {
                    let lon = p[1];
                    if (lon > 180) lon -= 360;
                    if (lon < -180) lon += 360;
                    return [lon, p[0]];
                })
            }
        }));

        const geojson = { type: "FeatureCollection", features };

        if (map.getSource("routes")) {
            map.getSource("routes").setData(geojson);
        } else {
            map.addSource("routes", { type: "geojson", data: geojson });
            map.addLayer({ id: "routes", type: "line", source: "routes",
                paint: {
                    "line-color": "#ce1531",
                    "line-width": ["case", ["boolean", ["feature-state", "hover"], false], 6, 3],
                    "line-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 1.0, 0.75]
                }
            });
        }
    }

    // ── Render ────────────────────────────────────────────────
    return (
        <div style={{ position: "absolute", top: 0, height: "100vh", width: "100%", zIndex: 0 }}>
            <div ref={mapRef} style={{ height: "100%", width: "100%" }} />

            {/* Cursor-volgende tooltip — positie via DOM, inhoud via React state */}
            <div ref={tooltipRef} className="map-info" style={{ display: hovered ? "block" : "none" }}>
                {hovered && (
                    <>
                        <div className="map-info-title">{hovered.name}</div>
                        {hovered.description && <div className="map-info-desc">{hovered.description}</div>}
                        {hovered.profilePhoto && (
                            <img src={hovered.profilePhoto} alt="" className="map-info-photo" />
                        )}
                        <div className="map-info-row">
                            <span className="map-info-label">Atleet</span>
                            <span>{hovered.displayName}</span>
                        </div>
                        <div className="map-info-row">
                            <span className="map-info-label">Datum</span>
                            <span>{formatDate(hovered.date)}</span>
                        </div>
                        <div className="map-info-row">
                            <span className="map-info-label">Afstand</span>
                            <span>{hovered.distance} km</span>
                        </div>
                        <div className="map-info-row">
                            <span className="map-info-label">Duur</span>
                            <span>{formatTime(hovered.duration)}</span>
                        </div>
                        <div className="map-info-row">
                            <span className="map-info-label">Snelheid</span>
                            <span>{getSpeed(hovered.distance * 1000, hovered.duration)} km/h</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
